// Pure date-window math for the Today date bar (v2.6).
//
// The bar shows the last `viewDays` days ending today. It starts at the
// 35-day *logging* window and grows via "Load older" so a user can review
// older entries (e.g. to reconcile a month's total). Logging itself stays
// capped at the window — these helpers only describe what's *viewable* and
// how far paging may reach; days older than the logging window render
// read-only in the UI.
//
// Extracted from App/TodayView (mirroring buildJiraOutputs / entryRules) so
// the boundary arithmetic is unit-testable without rendering.

import { addDays } from './format.js';

// Days that remain open for creating/editing entries, counting today. Mirrored
// by LogTimeForm's date picker (which offers exactly this many days).
export const LOG_WINDOW_DAYS = 35;

const DAY_MS = 86400000;

// Whole days from `a` to `b` (b - a), rounded so a DST hour never skews the
// count. Both are expected to be local-midnight Dates.
export function dayDiff(a, b) {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

// How many days the bar must span to include `date`, counting today itself:
// today → 1, yesterday → 2, today-34 → 35. Never less than 1.
export function daysToShow(date, today) {
  return Math.max(1, dayDiff(date, today) + 1);
}

// The oldest day still open for logging; days strictly before it are
// review-only. today-(windowDays-1) — e.g. today-34 for a 35-day window.
export function logWindowStart(today, windowDays = LOG_WINDOW_DAYS) {
  return addDays(today, -(windowDays - 1));
}

// True when `date` is older than the logging window (so it's review-only).
// today-34 is still loggable (== 35 days); today-35 is beyond.
export function isBeyondLogWindow(date, today, windowDays = LOG_WINDOW_DAYS) {
  return daysToShow(date, today) > windowDays;
}

// A pragmatic hard ceiling on how far the bar may grow, applied even when the
// review floor is unknown. Without it, an account with a missing/invalid
// `enabledOn` (a real case) would have no cap, so repeated "Load older" clicks
// (or arrow-key nav) would render one un-virtualized DOM chip per day and widen
// the summary fetch without bound. ~3 years is far beyond any real review need
// (ClockIn launched 2026-05) yet stops the pathological case. Kept above App's
// PERSISTED_DATE_FLOOR_DAYS (800) so a restored date near that floor still fits.
export const MAX_REVIEW_DAYS = 1100;

// The largest the bar may grow. Bounded by the review floor (employment start)
// when known — you can't have logged before you existed — and always by the
// absolute ceiling above. Never smaller than the default window.
export function maxViewDays(today, reviewFloor, minDays = LOG_WINDOW_DAYS) {
  if (!reviewFloor) return MAX_REVIEW_DAYS;
  return Math.min(MAX_REVIEW_DAYS, Math.max(minDays, daysToShow(reviewFloor, today)));
}

// Whether the "Load older" control should still be offered.
export function canLoadOlder(viewDays, maxDays) {
  return viewDays < maxDays;
}

// The next window size after a "Load older" step, clamped to the floor.
export function nextViewDays(viewDays, step, maxDays) {
  return Math.min(viewDays + step, maxDays);
}
